// Browser port of src/main/vehicles.js. Replaces:
//   - electron-store          -> src/renderer/storage.js (localStorage)
//   - serialport / dgram / net -> Web Serial API only (UDP/TCP unsupported in browser)
//   - mainWindow IPC           -> src/renderer/event_bus.js
//   - fs-based param XML       -> src/renderer/param_metadata_loader.js
//
// Keep MAVLink protocol handling identical to the original implementation.

import Vehicle from '../main/vehicle.js';
import WebSerial from './web_serial.js';
import store from './storage.js';
import { loadParametersXML } from './param_metadata_loader.js';
import eventBus from './event_bus.js';
import { mavlink20, MAVLink20Processor } from '../mavlink/mavlink.js';

// Convert a user-supplied signing key string into the 32-byte secret used by
// MAVLink2 signing. Accepts either 64 hex characters (raw key) or any other
// non-empty string, which is hashed with SHA256 (matches MAVProxy's
// `--passphrase` behavior). Returns null on empty/invalid input.
const deriveSigningSecret = (input) => {
    if (!input || typeof input !== 'string') return null;
    const trimmed = input.trim();
    if (!trimmed) return null;
    if (/^[0-9a-fA-F]{64}$/.test(trimmed)) {
        const out = new Uint8Array(32);
        for (let i = 0; i < 32; i++) {
            out[i] = parseInt(trimmed.substr(i * 2, 2), 16);
        }
        return out;
    }
    const bytes = new TextEncoder().encode(trimmed);
    return mavlink20.sha256(bytes);
};

const DefaultStreamRates = {
    rawSensors: 2,
    extendedStatus: 2,
    rcChannel: 2,
    rawCtrl: 0,
    position: 3,
    extra1: 10,
    extra2: 10,
    extra3: 3,
    params: 0,
    adsb: 0,
};

const normalizeStreamRates = (rates) => {
    const out = { ...DefaultStreamRates };
    if (!rates || typeof rates !== 'object') return out;
    Object.keys(DefaultStreamRates).forEach((key) => {
        const num = Number(rates[key]);
        if (Number.isFinite(num)) out[key] = num;
    });
    return out;
};

const normalizeMavlinkSettings = (rawSettings) => {
    const settings = rawSettings && typeof rawSettings === 'object' ? rawSettings : {};
    const normalized = {
        ...DefaultMavlinkSettings,
        ...settings,
    };

    const streamRateSources = {};
    if (settings.streamRateSources && typeof settings.streamRateSources === 'object') {
        Object.keys(settings.streamRateSources).forEach((id) => {
            streamRateSources[id] = normalizeStreamRates(settings.streamRateSources[id]);
        });
    }

    if (Object.keys(streamRateSources).length === 0) {
        const fallbackRates = normalizeStreamRates(settings.streamRates || DefaultStreamRates);
        streamRateSources[String(settings.streamSourceId ?? 0)] = fallbackRates;
    }

    const sourceId = String(settings.streamSourceId ?? Object.keys(streamRateSources)[0] ?? '0');
    if (!streamRateSources[sourceId]) streamRateSources[sourceId] = { ...DefaultStreamRates };

    normalized.streamSourceId = Number(sourceId);
    normalized.streamRateSources = streamRateSources;
    normalized.streamRates = streamRateSources[sourceId];
    return normalized;
};

const DefaultMavlinkSettings = {
    gcsSystemId: 255,
    emitHeartbeat: true,
    mavlinkLogPath: '',
    enableStreamRates: false,
    streamSourceId: 0,
    streamRates: { ...DefaultStreamRates },
    streamRateSources: {
        0: { ...DefaultStreamRates },
    },
};

const MavlinkStreamIDMapping = {
    rawSensors: mavlink20.MAV_DATA_STREAM_RAW_SENSORS,
    extendedStatus: mavlink20.MAV_DATA_STREAM_EXTENDED_STATUS,
    rcChannel: mavlink20.MAV_DATA_STREAM_RC_CHANNELS,
    position: mavlink20.MAV_DATA_STREAM_POSITION,
    extra1: mavlink20.MAV_DATA_STREAM_EXTRA1,
    extra2: mavlink20.MAV_DATA_STREAM_EXTRA2,
    extra3: mavlink20.MAV_DATA_STREAM_EXTRA3,
};

const SupportedVehicleTypes = new Set([
    mavlink20.MAV_TYPE_FIXED_WING,
    mavlink20.MAV_TYPE_FLAPPING_WING,
    mavlink20.MAV_TYPE_QUADROTOR,
    mavlink20.MAV_TYPE_COAXIAL,
    mavlink20.MAV_TYPE_HELICOPTER,
    mavlink20.MAV_TYPE_HEXAROTOR,
    mavlink20.MAV_TYPE_OCTOROTOR,
    mavlink20.MAV_TYPE_TRICOPTER,
    mavlink20.MAV_TYPE_DODECAROTOR,
    mavlink20.MAV_TYPE_GROUND_ROVER,
    mavlink20.MAV_TYPE_SURFACE_BOAT,
    mavlink20.MAV_TYPE_SUBMARINE,
    mavlink20.MAV_TYPE_ANTENNA_TRACKER,
    mavlink20.MAV_TYPE_VTOL_TAILSITTER_DUOROTOR,
    mavlink20.MAV_TYPE_VTOL_TAILSITTER_QUADROTOR,
    mavlink20.MAV_TYPE_VTOL_TILTROTOR,
    mavlink20.MAV_TYPE_VTOL_FIXEDROTOR,
    mavlink20.MAV_TYPE_VTOL_TAILSITTER,
    mavlink20.MAV_TYPE_VTOL_TILTWING,
    mavlink20.MAV_TYPE_VTOL_RESERVED5,
    mavlink20.MAV_TYPE_VTOL_RESERVED6,
]);

// Wrap WebSerial to expose the `.on('message', cb)` / `.removeListener(...)` /
// `.write(buf)` / `.connect()` / `.disconnect()` surface that the original
// Vehicles code (and MAVLink20Processor) expects.
class WebSerialTransport {
    constructor(serialPort, baudRate) {
        this.serial = new WebSerial(serialPort, baudRate);
        this._messageHandlers = new Set();
        this.serial.addMessageHandler((value) => {
            for (const h of this._messageHandlers) h(value);
        });
    }

    on(event, handler) {
        if (event === 'message') this._messageHandlers.add(handler);
        else if (event === 'error') this.serial.addErrorHandler(handler);
        else if (event === 'open') this.serial.addOpenHandler(handler);
        else if (event === 'close') this.serial.addCloseHandler(handler);
    }

    removeListener(event, handler) {
        if (event === 'message') this._messageHandlers.delete(handler);
        else if (event === 'error') this.serial.removeErrorHandler(handler);
        else if (event === 'open') this.serial.removeOpenHandler(handler);
        else if (event === 'close') this.serial.removeCloseHandler(handler);
    }

    connect() { return this.serial.connect(); }

    async disconnect() {
        if (this.serial.close) await this.serial.close();
        else if (this.serial.disconnect) await this.serial.disconnect();
    }

    // MAVLink20Processor.send writes here with an array of bytes / Buffer / etc.
    write(data) { return this.serial.write(data); }
}

// WebSocket-based MAVLink transport. The server is expected to push raw
// MAVLink bytes as binary frames (e.g. mavlink-router with --ws or any
// bridge that proxies a serial/UDP MAVLink stream over WebSocket).
class WebSocketTransport {
    constructor(url) {
        this.url = url;
        this.ws = null;
        this._messageHandlers = new Set();
        this._errorHandlers = new Set();
        this._openHandlers = new Set();
        this._closeHandlers = new Set();
    }

    on(event, handler) {
        if (event === 'message') this._messageHandlers.add(handler);
        else if (event === 'error') this._errorHandlers.add(handler);
        else if (event === 'open') this._openHandlers.add(handler);
        else if (event === 'close') this._closeHandlers.add(handler);
    }

    removeListener(event, handler) {
        if (event === 'message') this._messageHandlers.delete(handler);
        else if (event === 'error') this._errorHandlers.delete(handler);
        else if (event === 'open') this._openHandlers.delete(handler);
        else if (event === 'close') this._closeHandlers.delete(handler);
    }

    connect() {
        return new Promise((resolve, reject) => {
            try {
                const ws = new WebSocket(this.url);
                ws.binaryType = 'arraybuffer';
                this.ws = ws;
                let settled = false;
                ws.onopen = () => {
                    settled = true;
                    for (const h of this._openHandlers) h();
                    resolve();
                };
                ws.onerror = (event) => {
                    // Browsers intentionally hide the underlying reason on the
                    // error event for security. Inspect the network panel for
                    // the WebSocket handshake response (and any TLS warnings)
                    // to diagnose. Common causes: self-signed/untrusted TLS
                    // cert, server-side Origin/CORS rejection, or HTTP 4xx
                    // during the upgrade handshake.
                    console.error('[WebSocket] onerror', this.url, event);
                    const err = new Error(`WebSocket error: ${this.url} (see DevTools → Network → WS for details; for self-signed certs, open ${this.url.replace(/^ws/, 'http')} in this browser first to accept the certificate)`);
                    for (const h of this._errorHandlers) h(err);
                    if (!settled) {
                        settled = true;
                        reject(err);
                    }
                };
                ws.onclose = (event) => {
                    console.warn('[WebSocket] onclose', this.url, {
                        code: event.code,
                        reason: event.reason,
                        wasClean: event.wasClean,
                    });
                    for (const h of this._closeHandlers) h();
                    if (!settled) {
                        settled = true;
                        reject(new Error(`WebSocket closed before open (code=${event.code} reason=${event.reason || 'n/a'})`));
                    }
                };
                ws.onmessage = (event) => {
                    const data = event.data;
                    let bytes;
                    if (data instanceof ArrayBuffer) {
                        bytes = new Uint8Array(data);
                    } else if (ArrayBuffer.isView(data)) {
                        bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
                    } else if (typeof data === 'string') {
                        bytes = new TextEncoder().encode(data);
                    } else {
                        return;
                    }
                    for (const h of this._messageHandlers) h(bytes);
                };
            } catch (err) {
                reject(err);
            }
        });
    }

    disconnect() {
        if (this.ws) {
            try { this.ws.close(); } catch (_) {}
            this.ws = null;
        }
    }

    write(data) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        let buf;
        if (data instanceof ArrayBuffer) buf = data;
        else if (ArrayBuffer.isView(data)) buf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
        else if (Array.isArray(data)) buf = new Uint8Array(data).buffer;
        else if (typeof data === 'string') buf = new TextEncoder().encode(data).buffer;
        else return;
        this.ws.send(buf);
    }
}

class Vehicles {
    constructor() {
        this.vehicles = {};
        this.store = store;
        this.mavlinkSettings = this.getMavlinkSettings();
        this.parameterMetadata = {};
        this.mavlinkUtil = null;
        this.systemId = this.mavlinkSettings.gcsSystemId || 255;
        this.componentId = 0;
        this.mavlinkProcessor = new MAVLink20Processor(null, this.systemId, this.componentId);
        this.timeoutDuration = 1000;
        this.initialConnectionNoticeDuration = 3000;
        this.disconnectNoticeDuration = 5000;
        this.utilTimestamp = 0;
        this.connectionOpenedAt = 0;
        this.hasReceivedConnectionData = false;
        this.communicationLossNotified = false;
        this.lastConnectionConfig = null;
        this.rebootReconnectState = null;
        this.pendingPostRebootParamRefresh = null;
        this.pendingRecoveryParamRefresh = new Set();
        this.startTimeoutCheck();
        this.gcsHeartbeatInterval();
    }

    loadMetadata = async () => {
        try {
            this.parameterMetadata = await loadParametersXML();
        } catch (err) {
            console.error('Error loading parameter metadata:', err);
        }
    };

    startTimeoutCheck() {
        setInterval(() => {
            const lastDataOrOpen = this.utilTimestamp || this.connectionOpenedAt;
            const silenceDuration = lastDataOrOpen ? Date.now() - lastDataOrOpen : Infinity;
            const isHealthy = silenceDuration < this.timeoutDuration;
            this.sendToMainWindow('flight-controller-connection-health', isHealthy);
            if (this.mavlinkUtil
                && this.connectionOpenedAt > 0
                && !this.rebootReconnectState
                && !this.communicationLossNotified
                && silenceDuration >= (this.hasReceivedConnectionData
                    ? this.disconnectNoticeDuration
                    : this.initialConnectionNoticeDuration)) {
                this.communicationLossNotified = true;
                // A persistent transport (notably a WebSocket bridge) can
                // stay open while the flight controller behind it reboots.
                // Remember the existing vehicles so their cached parameters
                // are refreshed when their heartbeat returns.
                Object.keys(this.vehicles).forEach((mavId) => {
                    this.pendingRecoveryParamRefresh.add(String(mavId));
                });
                eventBus.emit('connection-status', {
                    success: false,
                    disconnected: true,
                    message: this.hasReceivedConnectionData
                        ? 'Disconnected: no MAVLink data received for 5 seconds.'
                        : 'Connection failed: no MAVLink data received for 3 seconds. Check the baud rate.',
                });
            }
        }, this.timeoutDuration);
    }

    gcsHeartbeatInterval() {
        setInterval(() => {
            if (this.mavlinkSettings.emitHeartbeat && this.mavlinkUtil) {
                this.sendMavlinkHeartbeat();
            }
        }, 1000);
    }

    sendToMainWindow(channel, ...args) {
        eventBus.emit(channel, ...args);
    }

    cancelRebootReconnect(finalStatus = null) {
        if (this.rebootReconnectState?.timer) {
            clearTimeout(this.rebootReconnectState.timer);
        }
        this.rebootReconnectState = null;
        if (finalStatus?.phase === 'failed' || finalStatus?.phase === 'cancelled') {
            this.clearPostRebootParamRefresh();
        }
        if (finalStatus) eventBus.emit('reboot-reconnect-status', finalStatus);
    }

    clearPostRebootParamRefresh() {
        if (this.pendingPostRebootParamRefresh?.timer) {
            clearTimeout(this.pendingPostRebootParamRefresh.timer);
        }
        this.pendingPostRebootParamRefresh = null;
    }

    startParamRefresh(mavId, delayMs = 0) {
        const normalizedMavId = String(mavId);
        const current = this.pendingPostRebootParamRefresh;
        if (!current || current.mavId !== normalizedMavId) {
            this.clearPostRebootParamRefresh();
            this.pendingPostRebootParamRefresh = {
                mavId: normalizedMavId,
                notBefore: Date.now() + delayMs,
                attempts: 0,
                maxAttempts: 5,
                timer: null,
            };
        }
        this.requestPostRebootParams(this.getVehicleByMavId(normalizedMavId));
    }

    requestPostRebootParams(vehicle) {
        const state = this.pendingPostRebootParamRefresh;
        if (!state || !vehicle || state.mavId !== String(vehicle.mavId)) return;
        if (state.timer) {
            clearTimeout(state.timer);
            state.timer = null;
        }
        const waitMs = state.notBefore - Date.now();
        if (waitMs > 0) {
            state.timer = setTimeout(() => {
                if (this.pendingPostRebootParamRefresh !== state) return;
                this.requestPostRebootParams(this.getVehicleByMavId(state.mavId));
            }, waitMs);
            return;
        }
        if (state.attempts >= state.maxAttempts) {
            console.warn('Post-reboot parameter refresh did not receive a response.');
            this.clearPostRebootParamRefresh();
            return;
        }

        if (state.attempts === 0) {
            vehicle.params = {};
            this.sendToMainWindow('param-list', vehicle.mavId, vehicle.params);
        }
        state.attempts += 1;
        vehicle.requestParamList();

        // A request sent immediately after the first heartbeat can be ignored
        // while ArduPilot is still finishing startup. Retry until a PARAM_VALUE
        // confirms that the parameter stream has actually begun.
        state.timer = setTimeout(() => {
            if (this.pendingPostRebootParamRefresh !== state) return;
            this.requestPostRebootParams(this.getVehicleByMavId(state.mavId));
        }, 1500);
    }

    scheduleRebootReconnect(delayMs = 2000) {
        const state = this.rebootReconnectState;
        if (!state) return;
        if (state.timer) clearTimeout(state.timer);
        state.timer = setTimeout(() => this.attemptRebootReconnect(), delayMs);
    }

    startRebootReconnect() {
        if (!this.lastConnectionConfig) return;
        this.cancelRebootReconnect();
        this.rebootReconnectState = {
            config: { ...this.lastConnectionConfig },
            attempts: 0,
            maxAttempts: 10,
            readyAfter: Date.now() + 2500,
            timer: null,
        };
        eventBus.emit('reboot-reconnect-status', {
            phase: 'waiting',
            attempt: 0,
            maxAttempts: this.rebootReconnectState.maxAttempts,
            message: 'Waiting for the flight controller to reboot…',
        });
        this.scheduleRebootReconnect(5000);
    }

    async resolveRebootSerialPort(config) {
        const ports = await WebSerial.listPorts();
        if (ports.includes(config.port)) return config.port;
        const previousInfo = config.port?.getInfo?.() || {};
        return ports.find((candidate) => {
            const info = candidate.getInfo?.() || {};
            return info.usbVendorId === previousInfo.usbVendorId
                && info.usbProductId === previousInfo.usbProductId;
        }) || config.port;
    }

    async attemptRebootReconnect() {
        const state = this.rebootReconnectState;
        if (!state) return;
        state.timer = null;

        if (Date.now() >= state.readyAfter && Date.now() - this.utilTimestamp < 1500) {
            this.cancelRebootReconnect({ phase: 'success', message: 'Flight controller reconnected.' });
            eventBus.emit('connection-status', { success: true, reconnected: true });
            return;
        }
        if (state.attempts >= state.maxAttempts) {
            this.cancelRebootReconnect({
                phase: 'failed',
                message: 'Flight controller did not reconnect after reboot.',
            });
            eventBus.emit('connection-status', {
                success: false,
                message: 'Flight controller did not reconnect after reboot.',
            });
            return;
        }

        state.attempts += 1;
        eventBus.emit('reboot-reconnect-status', {
            phase: 'reconnecting',
            attempt: state.attempts,
            maxAttempts: state.maxAttempts,
            message: `Reconnecting to the flight controller (${state.attempts}/${state.maxAttempts})…`,
        });
        const config = { ...state.config };
        try {
            if (config.type === 'serial') {
                config.port = await this.resolveRebootSerialPort(config);
                state.config.port = config.port;
            }
            await this.disconnect();
            await new Promise((resolve) => setTimeout(resolve, 250));
            if (this.rebootReconnectState !== state) return;
            await this.connect(config, { reconnecting: true });
            this.scheduleRebootReconnect(2000);
        } catch (error) {
            console.warn(`Reboot reconnect attempt ${state.attempts} failed:`, error);
            if (this.rebootReconnectState === state) this.scheduleRebootReconnect(2000);
        }
    }

    reboot(mavId) {
        const vehicle = this.getVehicleByMavId(mavId);
        if (!vehicle) return;
        this.pendingPostRebootParamRefresh = {
            mavId: String(mavId),
            notBefore: Date.now() + 2500,
            attempts: 0,
            maxAttempts: 5,
            timer: null,
        };
        vehicle.reboot();
        this.startRebootReconnect();
    }

    async connect(port, options = {}) {
        if (!options.reconnecting) {
            this.cancelRebootReconnect(this.rebootReconnectState
                ? { phase: 'cancelled', message: 'Reboot reconnect cancelled.' }
                : null);
            this.lastConnectionConfig = { ...port };
            this.clearPostRebootParamRefresh();
        }
        // Reset traffic timestamp; only real MAVLink messages should mark the
        // link as healthy. Seeding this with Date.now() here would make the
        // health check report a healthy link for ~1 s even when the transport
        // open ultimately fails.
        this.utilTimestamp = 0;
        this.connectionOpenedAt = 0;
        this.hasReceivedConnectionData = false;
        this.communicationLossNotified = false;

        if (this.mavlinkUtil) {
            await this.disconnect();
        }

        if (port.type === 'serial') {
            const serialPort = port.port;
            if (!serialPort || typeof serialPort === 'string') {
                const msg = 'No serial port selected. Click + to authorize one first.';
                console.error(msg);
                eventBus.emit('connection-status', { success: false, message: msg });
                return;
            }
            this.mavlinkUtil = new WebSerialTransport(serialPort, port.baudRate || 115200);
        } else if (port.type === 'websocket') {
            if (!port.url) {
                const msg = 'WebSocket connection requires a url';
                console.error(msg);
                eventBus.emit('connection-status', { success: false, message: msg });
                return;
            }
            this.mavlinkUtil = new WebSocketTransport(port.url);
        } else {
            const msg = `Unsupported port type: ${port.type}. Supported: "serial", "websocket".`;
            console.error(msg);
            eventBus.emit('connection-status', { success: false, message: msg });
            return;
        }

        // Configure MAVLink2 signing on the processor before opening the
        // transport so the very first outgoing heartbeat is signed and the
        // autopilot won't reject the GCS with "Need to use support signing key".
        // Keep serial connections unsigned by default. Only websocket links
        // should consume UI-provided signing keys.
        const secret = port.type === 'websocket'
            ? deriveSigningSecret(port.signingKey)
            : null;
        if (secret) {
            this.mavlinkProcessor.signing.secret_key = secret;
            this.mavlinkProcessor.signing.sign_outgoing = true;
            this.mavlinkProcessor.signing.timestamp = Math.max(
                this.mavlinkProcessor.signing.timestamp || 1,
                1,
            );
        } else {
            this.mavlinkProcessor.signing.secret_key = new Uint8Array();
            this.mavlinkProcessor.signing.sign_outgoing = false;
        }

        this.mavlinkUtilDataListener = (msg) => {
            if (!this.mavlinkUtil) return;
            const data = new Uint8Array(msg);
            const messages = this.mavlinkProcessor.parseBuffer(data);
            this.handleMavlinkMessages(messages);
        };
        this.mavlinkUtil.on('message', this.mavlinkUtilDataListener);

        // Detect early transport close (e.g. serial port busy, USB unplugged,
        // remote refused) and surface as a connection failure instead of a
        // silent success that quietly drops a moment later.
        const transport = this.mavlinkUtil;
        const openAt = Date.now();
        this.mavlinkUtilCloseListener = async () => {
            if (this.mavlinkUtil !== transport) return;
            const sinceOpen = Date.now() - openAt;
            const sawTraffic = this.utilTimestamp > openAt;
            if (!sawTraffic && !this.rebootReconnectState) {
                const msg = sinceOpen < 500
                    ? 'Port opened then closed immediately. It may be in use by another app.'
                    : 'Connection dropped before any data was received.';
                eventBus.emit('connection-status', { success: false, message: msg });
            }
            await this.disconnect();
            if (this.rebootReconnectState) this.scheduleRebootReconnect(500);
        };
        transport.on('close', this.mavlinkUtilCloseListener);

        // Bind the transport to the processor before awaiting connect() so that
        // any concurrent send (e.g. the GCS heartbeat interval) sees a usable
        // transport rather than an undefined `file`. If connect() rejects we
        // clean up below.
        this.mavlinkProcessor.file = this.mavlinkUtil;
        try {
            await this.mavlinkUtil.connect();
            this.connectionOpenedAt = Date.now();
            this.lastConnectionConfig = { ...port };
        } catch (err) {
            await this.disconnect();
            throw err;
        }
    }

    async disconnect(options = {}) {
        if (options.cancelReconnect) {
            this.cancelRebootReconnect(this.rebootReconnectState
                ? { phase: 'cancelled', message: 'Reboot reconnect cancelled.' }
                : null);
            this.lastConnectionConfig = null;
            this.clearPostRebootParamRefresh();
        }
        this.utilTimestamp = 0;
        this.connectionOpenedAt = 0;
        this.hasReceivedConnectionData = false;
        this.communicationLossNotified = false;
        const transport = this.mavlinkUtil;
        if (transport) {
            this.mavlinkUtil = null;
            try {
                if (this.mavlinkUtilCloseListener) {
                    try { transport.removeListener('close', this.mavlinkUtilCloseListener); } catch (_) {}
                    this.mavlinkUtilCloseListener = null;
                }
                if (this.mavlinkProcessor) this.mavlinkProcessor.file = null;
                if (this.mavlinkUtilDataListener) {
                    transport.removeListener('message', this.mavlinkUtilDataListener);
                    this.mavlinkUtilDataListener = null;
                }
                await transport.disconnect();
            } catch (err) {
                console.warn('Error during disconnect:', err);
            }
        }
        // Reset signing state so a subsequent connect without a key doesn't
        // accidentally inherit the previous secret_key/timestamp.
        if (this.mavlinkProcessor && this.mavlinkProcessor.signing) {
            this.mavlinkProcessor.signing.secret_key = new Uint8Array();
            this.mavlinkProcessor.signing.sign_outgoing = false;
        }
        // Drop any vehicles discovered through the previous link so that
        // reconnecting on a different interface starts from a clean slate
        // (no stale HEARTBEAT/params/mode list lingering in window.mav).
        this.vehicles = {};
        this.pendingRecoveryParamRefresh.clear();
        this.sendToMainWindow('vehicles-cleared');
        this.sendToMainWindow('mav-id-list', this.getMavIdList());
        this.sendToMainWindow('mode-list', {});
    }

    addVehicle(mavType, mavId, componentId) {
        if (!this.vehicles[mavId]) {
            this.vehicles[mavId] = new Vehicle(
                mavType,
                mavId,
                componentId,
                this.sendMavlinkMsg.bind(this),
                this.sendToMainWindow.bind(this),
            );
            if (this.mavlinkSettings.enableStreamRates) {
                this.sendStreamRatesRequest(this.mavlinkSettings, this.vehicles[mavId]);
            }
        }
        return this.vehicles[mavId];
    }

    sendMavlinkMsg(msg) {
        if (this.mavlinkUtil && this.mavlinkProcessor) {
            this.mavlinkProcessor.send(msg);
        }
    }

    getMavIdList() {
        const result = {};
        Object.keys(this.vehicles).forEach((key) => {
            result[key] = this.vehicles[key].getMavTypeName();
        });
        return result;
    }

    getVehicleByMavId(mavId) { return this.vehicles[mavId]; }
    isExistsByMavId(mavId) { return Object.prototype.hasOwnProperty.call(this.vehicles, mavId); }

    sendMavlinkHeartbeat() {
        const heartbeat = new mavlink20.messages.heartbeat(
            mavlink20.MAV_TYPE_GCS,
            mavlink20.MAV_AUTOPILOT_GENERIC,
            0, 0,
            mavlink20.MAV_STATE_ACTIVE,
            3,
        );
        this.sendMavlinkMsg(heartbeat);
    }

    sendStreamRatesRequest(settings = this.mavlinkSettings, targetVehicle = null) {
        if (!settings?.enableStreamRates || !this.mavlinkUtil) return;
        const sourceId = String(settings.streamSourceId ?? 0);
        const rates = (settings.streamRateSources && settings.streamRateSources[sourceId])
            ? settings.streamRateSources[sourceId]
            : settings.streamRates;
        if (!rates) return;

        // REQUEST_DATA_STREAM is target-addressed. Broadcasting it to system 0
        // can make every MAVLink endpoint on a shared/virtual link change its
        // output rates at once and saturate the transport. Only request rates
        // from autopilots that Lite GCS has actually discovered.
        const targets = targetVehicle ? [targetVehicle] : Object.values(this.vehicles);
        targets.forEach((vehicle) => {
            if (!vehicle || !Number.isInteger(Number(vehicle.mavId))) return;
            Object.keys(rates).forEach((key) => {
                if (MavlinkStreamIDMapping[key] === undefined || typeof rates[key] !== 'number') return;
                const req = new mavlink20.messages.request_data_stream(
                    Number(vehicle.mavId),
                    Number(vehicle.componentId) || 1,
                    MavlinkStreamIDMapping[key],
                    rates[key],
                    1,
                );
                this.sendMavlinkMsg(req);
            });
        });
    }

    handleMavlinkMessages(messages) {
        // Wrong baud rates can still produce arbitrary serial bytes. The
        // parser returns [] for those chunks; they must not count as valid
        // MAVLink traffic or they will keep the no-data timeout alive forever.
        if (!messages || messages.length === 0) return;
        this.sendToMainWindow('flight-controller-connection-timestamp', Date.now());
        this.utilTimestamp = Date.now();
        this.hasReceivedConnectionData = true;
        if (this.communicationLossNotified) {
            this.communicationLossNotified = false;
            eventBus.emit('connection-status', { success: true, recovered: true });
        }
        if (this.rebootReconnectState && Date.now() >= this.rebootReconnectState.readyAfter) {
            this.cancelRebootReconnect({ phase: 'success', message: 'Flight controller reconnected.' });
            eventBus.emit('connection-status', { success: true, reconnected: true });
        }

        messages.forEach((message) => {
            if (!(message._header !== undefined && Number.isInteger(message._header.srcComponent))) return;
            const name = message._name;
            const srcSystem = message._header.srcSystem;
            const srcComponent = message._header.srcComponent;
            const msgId = message._header.msgId;
            // FILE_TRANSFER_PROTOCOL is a high-rate burst stream during
            // FTP downloads (~30 packets/s for log files). It is fully
            // handled by `vehicle.mavlinkFTP.update(message)` below;
            // forwarding it to the renderer's generic 'mavlink-message'
            // bus also triggers a window-wide "mav" CustomEvent on every
            // packet, which re-renders subscribed React components and
            // throttles bulk FTP throughput by ~10x. Skip the generic
            // dispatch for FTP frames.
            if (msgId !== mavlink20.MAVLINK_MSG_ID_FILE_TRANSFER_PROTOCOL) {
                const fields = {};
                message.fieldnames.forEach((field) => { fields[field] = message[field]; });
                this.sendToMainWindow('mavlink-message', name, fields, message._header);
            }

            switch (msgId) {
                case mavlink20.MAVLINK_MSG_ID_HEARTBEAT: {
                    // Skip heartbeats from non-autopilot components on the same
                    // system (SiK telemetry radios, GCS-like peripherals).
                    // They report autopilot=MAV_AUTOPILOT_INVALID(8) and would
                    // otherwise spam mav-id-list / mode-list / vehicle add.
                    if (message.autopilot === mavlink20.MAV_AUTOPILOT_INVALID) {
                        break;
                    }
                    // Ignore non-vehicle heartbeat types (GCS/companion/etc.)
                    // to avoid creating UNKNOWN system entries (e.g. sysid 255)
                    // on relay links.
                    if (!SupportedVehicleTypes.has(message.type)) {
                        break;
                    }
                    let createdVehicle = false;
                    const refreshAfterRecovery = this.pendingRecoveryParamRefresh.delete(String(srcSystem));
                    if (srcComponent > 0 && srcComponent < 255 && !this.isExistsByMavId(srcSystem)) {
                        const vehicle = this.addVehicle(message.type, srcSystem, srcComponent);
                        if (vehicle) {
                            createdVehicle = true;
                            if (this.pendingPostRebootParamRefresh?.mavId === String(srcSystem)) {
                                this.requestPostRebootParams(vehicle);
                            } else {
                                vehicle.requestParamList();
                            }
                        }
                    }
                    if (this.isExistsByMavId(srcSystem)) {
                        const vehicle = this.getVehicleByMavId(srcSystem);
                        if (refreshAfterRecovery) {
                            // The bridge never disconnected, so this is not a
                            // newly-created Vehicle and the normal discovery
                            // request above did not run. Treat the returning
                            // heartbeat as a new parameter session.
                            this.startParamRefresh(srcSystem, 500);
                        } else if (this.pendingPostRebootParamRefresh?.mavId === String(srcSystem)
                            && Date.now() >= this.pendingPostRebootParamRefresh.notBefore) {
                            if (!createdVehicle) {
                                this.requestPostRebootParams(vehicle);
                            }
                        }
                        this.sendToMainWindow('mode-list', vehicle.getAvailableFlightModes());
                        this.sendToMainWindow('mav-id-list', this.getMavIdList());
                    }
                    break;
                }
                case mavlink20.MAVLINK_MSG_ID_PARAM_VALUE: {
                    if (!this.isExistsByMavId(srcSystem)) break;
                    if (this.pendingPostRebootParamRefresh?.mavId === String(srcSystem)
                        && this.pendingPostRebootParamRefresh.attempts > 0
                        && message.param_index !== 65535) {
                        // Keep receiving the full stream, but stop retrying as
                        // soon as an indexed PARAM_VALUE proves it accepted
                        // PARAM_REQUEST_LIST. A 65535 index is only a single
                        // parameter update and must not stop the full refresh.
                        this.clearPostRebootParamRefresh();
                    }
                    const vehicle = this.getVehicleByMavId(srcSystem);
                    let metaData = {};
                    const paramName = message.param_id.replace(/\x00/g, '');
                    const cls = vehicle.getMavTypeTargetName();
                    // Try vehicle-specific key first to avoid library params being
                    // overwritten across vehicles (e.g. RC*_OPTION differs per firmware).
                    if (cls && this.parameterMetadata[`${cls}:${paramName}`] !== undefined) {
                        metaData = this.parameterMetadata[`${cls}:${paramName}`];
                    } else if (this.parameterMetadata[paramName] !== undefined) {
                        metaData = this.parameterMetadata[paramName];
                    }
                    const param = vehicle.storeParam(paramName, message.param_value, message.param_type, metaData);
                    const total = message.param_count;
                    // Use param_index from the message so progress reflects the current download
                    // even when params dict is already populated (e.g. user clicks Refresh).
                    const received = message.param_index + 1;
                    const isStreamUpdate = message.param_index !== 65535;
                    if (isStreamUpdate) {
                        this.sendToMainWindow('param-progress', srcSystem, received, total);
                    }
                    if (message.param_index === 65535) {
                        this.sendToMainWindow('param', srcSystem, paramName, param);
                        this.sendToMainWindow('param-list', srcSystem, vehicle.params);
                    } else if (received === total) {
                        this.sendToMainWindow('param-list', srcSystem, vehicle.params);
                    } else if (received % 50 === 0) {
                        // Periodic progressive update so the table populates as params stream in
                        this.sendToMainWindow('param-list', srcSystem, vehicle.params);
                    }
                    break;
                }
                case mavlink20.MAVLINK_MSG_ID_FILE_TRANSFER_PROTOCOL: {
                    const vehicle = this.getVehicleByMavId(srcSystem);
                    if (vehicle) vehicle.mavlinkFTP.update(message);
                    break;
                }
                default: break;
            }
        });
    }

    getMavlinkSettings() {
        const loaded = this.store.get('mavlink-settings') || { ...DefaultMavlinkSettings };
        return normalizeMavlinkSettings(loaded);
    }

    saveMavlinkSettings(settings) {
        const previous = this.mavlinkSettings;
        const normalized = normalizeMavlinkSettings(settings);
        const previousSourceId = String(previous?.streamSourceId ?? 0);
        const nextSourceId = String(normalized.streamSourceId ?? 0);
        const previousRates = previous?.streamRateSources?.[previousSourceId] || previous?.streamRates || {};
        const nextRates = normalized.streamRateSources?.[nextSourceId] || normalized.streamRates || {};
        const streamRequestChanged = previous?.enableStreamRates !== normalized.enableStreamRates
            || previousSourceId !== nextSourceId
            || Object.keys(MavlinkStreamIDMapping).some((key) => previousRates[key] !== nextRates[key]);
        this.store.set('mavlink-settings', normalized);
        this.mavlinkSettings = normalized;
        this.systemId = normalized.gcsSystemId || 255;
        if (this.mavlinkProcessor) this.mavlinkProcessor.srcSystem = this.systemId;

        if (this.mavlinkUtil && streamRequestChanged) {
            if (normalized.enableStreamRates) {
                this.sendStreamRatesRequest(normalized);
            }
            // Disabling means Lite GCS no longer overrides stream rates. Do
            // not send start_stop=0: that would stop ArduPilot's existing
            // channel streams and make the live connection appear dead.
        }
    }
}

const vehicles = new Vehicles();
export default vehicles;
export { Vehicles };
